import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const roleHome = {
  super_admin: "/admin",
  manager: "/admin",
  owner: "/admin",
  partner: "/partner",
  staff: "/staff",
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("expired") || sessionStorage.getItem("kps_session_expired")) {
      setExpired(true);
      sessionStorage.removeItem("kps_session_expired");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      nav(roleHome[res.user.role] || "/admin");
    } else {
      toast.error(res.error);
    }
  };

  const quickFill = (em) => {
    setEmail(em);
    setPassword("");
    setTimeout(() => document.getElementById("password")?.focus(), 0);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors" data-testid="back-home-link">
          <ArrowLeft size={16} /> Back to website
        </Link>
        <div className="glass gold-border rounded-2xl p-8 animate-fade-up">
          <div className="mb-8 flex justify-center">
            <Logo size="lg" />
          </div>
          <div className="mb-6 text-center">
            <div className="overline-label">Team Access</div>
            <h1 className="mt-2 font-serif text-2xl font-bold">Staff & Admin Login</h1>
          </div>
          {expired && (
            <div className="mb-5 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-xs text-amber-400" data-testid="session-expired-notice">
              <AlertTriangle size={14} /> Your session expired. Please sign in again.
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@kingssalon.in" required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn"
              className="w-full bg-gold text-primary-foreground hover:bg-gold-hover font-semibold h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <div className="mt-6 rounded-lg border border-border bg-secondary/40 p-3 text-xs text-muted-foreground">
            <div className="mb-2 flex items-center gap-1.5 font-semibold text-gold"><ShieldCheck size={13}/> Accounts (tap to fill email)</div>
            <div className="grid grid-cols-1 gap-1">
              {[
                ["Admin", "mannyshah2806@gmail.com"],
                ["Owner", "owner@kingssalon.in"],
                ["Partner", "partner@kingssalon.in"],
                ["Staff", "staff@kingssalon.in"],
              ].map(([label, em]) => (
                <button key={em} type="button" onClick={() => quickFill(em)}
                  data-testid={`quickfill-${label.toLowerCase()}`}
                  className="flex justify-between rounded px-2 py-1 text-left hover:bg-secondary transition-colors">
                  <span className="font-medium text-foreground">{label}</span>
                  <span className="font-mono">{em}</span>
                </button>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-muted-foreground">Enter your password to sign in.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
